/**
 * Bundle 164 A3 - Testimonials Pending Review tile.
 *
 * Live count of `testimonial_consents` rows with
 * `ai_draft_status="pending_admin_review"` (main count) and
 * a sub-count for `insufficient_source`. Clicking the tile
 * deep-links to the Bundle 156b approval queue at
 * `/admin/testimonials/approval-queue` (corrected in the
 * Bundle 164 hotfix 2 - the earlier route slug never existed
 * in the FE router).
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { MessageSquareQuote } from 'lucide-react';
import api from '@/utils/api';

const APPROVAL_QUEUE_ROUTE = '/admin/testimonials/approval-queue';

const TestimonialsPendingReviewTile = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data: body } = await api.get(
          '/admin/incidents/tiles/testimonials-pending-review');
        if (alive) setData(body);
      } catch (_) {
        // noop - tile hides gracefully
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);
  const pending = data?.pending_review ?? 0;
  const insufficient = data?.insufficient_source ?? 0;
  return (
    <Link
      to={data?.deep_link || APPROVAL_QUEUE_ROUTE}
      className="block group"
      data-testid="testimonials-pending-tile-link"
    >
      <Card
        className={
          'border shadow-soft transition-all hover:shadow-md '
          + (pending > 0
            ? 'border-amber-200 hover:border-amber-300'
            : 'border-neutral-200 hover:border-primary-300')
        }
        data-testid="testimonials-pending-tile"
      >
        <CardContent className="p-4">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <MessageSquareQuote className="h-4 w-4 text-primary-800" />
                <div className="text-[10px] uppercase tracking-widest text-secondary-700">
                  Testimonials pending review
                </div>
              </div>
              <div className="text-sm text-primary-900 font-semibold mb-2">
                {loading ? 'Loading.'
                  : pending > 0
                    ? 'Draft(s) awaiting your approval'
                    : 'Queue is clear'}
              </div>
              <div className="grid grid-cols-2 gap-6 pt-1">
                <div
                  data-testid="testimonials-pending-tile-pending"
                >
                  <div
                    className={`text-2xl font-semibold ${
                      pending > 0 ? 'text-amber-700' : 'text-neutral-500'
                    }`}
                  >
                    {pending}
                  </div>
                  <div className="text-[10px] uppercase tracking-widest text-neutral-500">
                    Pending review
                  </div>
                </div>
                <div
                  data-testid="testimonials-pending-tile-insufficient"
                >
                  <div
                    className={`text-2xl font-semibold ${
                      insufficient > 0
                        ? 'text-neutral-700' : 'text-neutral-500'
                    }`}
                  >
                    {insufficient}
                  </div>
                  <div className="text-[10px] uppercase tracking-widest text-neutral-500">
                    Insufficient source
                  </div>
                </div>
              </div>
            </div>
            <div className="text-xs text-secondary-700 group-hover:text-primary-900 whitespace-nowrap">
              Open queue →
            </div>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
};

export default TestimonialsPendingReviewTile;
